import { useEffect, useRef } from 'react';
import type { RefObject } from 'react';

const SwipeDismissDistance: number = 0.25;

const SwipeDismissVelocity: number = 0.5;

/**
 *	Every sheet that can be swiped right now. Sheets nest: a dropdown's sheet
 *	opened inside a modal's sheet sits in the modal's DOM, and touch events
 *	reach both. The innermost sheet a drag starts in owns it, so the ones
 *	around it don't close along with it.
 */
const SwipeRoots: Set<HTMLElement> = new Set();

function insideInnerSheet(
	target: EventTarget | null,
	root: HTMLElement
): boolean {
	let node: Element | null = target instanceof Element ? target : null;

	while (node && node !== root) {
		if (node instanceof HTMLElement && SwipeRoots.has(node)) {
			return true;
		}

		node = node.parentElement;
	}

	return false;
}

function scrolledAway(target: EventTarget | null, root: HTMLElement): boolean {
	let node: Element | null = target instanceof Element ? target : null;

	while (node && root.contains(node)) {
		if (node.scrollTop > 0) {
			return true;
		}

		node = node.parentElement;
	}

	return false;
}

export function useSwipeToDismiss(
	element: RefObject<HTMLElement | null>,
	enabled: boolean,
	onDismiss: () => void
): void {
	const dismiss = useRef<() => void>(onDismiss);

	dismiss.current = onDismiss;

	useEffect(() => {
		const root: HTMLElement | null = element.current;

		if (!enabled || !root) {
			return;
		}

		let startY: number = 0;
		let lastY: number = 0;
		let lastTime: number = 0;
		let velocity: number = 0;
		let tracking: boolean = false;
		let dragging: boolean = false;

		function release(): void {
			if (!root) {
				return;
			}

			root.style.removeProperty('transition');
			root.style.removeProperty('translate');
		}

		function handleStart(event: TouchEvent): void {
			const touch: Touch | null = event.touches.item(0);

			if (event.touches.length !== 1 || !root || !touch) {
				tracking = false;
				return;
			}

			tracking =
				!insideInnerSheet(event.target, root) &&
				!scrolledAway(event.target, root);
			dragging = false;
			startY = lastY = touch.clientY;
			lastTime = event.timeStamp;
			velocity = 0;
		}

		function handleMove(event: TouchEvent): void {
			const touch: Touch | null = event.touches.item(0);

			if (!tracking || !root || !touch) {
				return;
			}

			const y: number = touch.clientY;
			const distance: number = y - startY;

			if (!dragging) {
				if (distance < 0) {
					tracking = false;
				}

				if (distance <= 0) {
					return;
				}

				dragging = true;
				root.style.setProperty('transition', 'none');
			}

			const elapsed: number = event.timeStamp - lastTime;

			if (elapsed > 0) {
				velocity = (y - lastY) / elapsed;
			}

			lastY = y;
			lastTime = event.timeStamp;

			event.preventDefault();
			root.style.setProperty('translate', `0 ${Math.max(distance, 0)}px`);
		}

		function handleEnd(): void {
			if (!dragging || !root) {
				tracking = false;
				return;
			}

			const distance: number = lastY - startY;
			const height: number = root.getBoundingClientRect().height;

			tracking = dragging = false;

			if (
				distance > height * SwipeDismissDistance ||
				velocity > SwipeDismissVelocity
			) {
				dismiss.current();
			}

			release();
		}

		function handleCancel(): void {
			tracking = dragging = false;
			release();
		}

		SwipeRoots.add(root);

		root.addEventListener('touchstart', handleStart, { passive: true });
		root.addEventListener('touchmove', handleMove, { passive: false });
		root.addEventListener('touchend', handleEnd);
		root.addEventListener('touchcancel', handleCancel);

		return () => {
			SwipeRoots.delete(root);
			root.removeEventListener('touchstart', handleStart);
			root.removeEventListener('touchmove', handleMove);
			root.removeEventListener('touchend', handleEnd);
			root.removeEventListener('touchcancel', handleCancel);
			release();
		};
	}, [element, enabled]);
}
